import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  creerRepas,
  filtrerIngredients,
  filtrerRecettes,
  listerIngredients,
  listerRecettes,
  lireRepas,
  majRepas,
  resoudreComposition,
  supprimerRepas,
} from '../../db/repo'
import { fromDatetimeLocalValue, nowInstant, toDatetimeLocalValue } from '../../domain/time'
import {
  MEAL_TYPES,
  MEAL_TYPE_LABELS,
  UNITES,
  UNITE_LABELS,
  type MealSource,
  type MealType,
  type Unite,
} from '../../domain/types'
import {
  BoutonSupprimer,
  Carte,
  Champ,
  ChampNombre,
  Entete,
  IconeCroix,
  Vide,
} from '../composants'

export function PageRepas() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const edition = Boolean(id)

  const ingredients = useLiveQuery(() => listerIngredients(), [])
  const recettes = useLiveQuery(() => listerRecettes(), [])

  const [at, setAt] = useState(() => toDatetimeLocalValue(nowInstant()))
  const [type, setType] = useState<MealType>(() => {
    const t = params.get('type')
    return (MEAL_TYPES as readonly string[]).includes(t ?? '') ? (t as MealType) : 'dejeuner'
  })
  const [sources, setSources] = useState<MealSource[]>([])
  const [notes, setNotes] = useState('')
  const [recherche, setRecherche] = useState('')
  const [charge, setCharge] = useState(false)

  // Chargement d'un repas existant
  useEffect(() => {
    if (!id) return
    void lireRepas(id).then((m) => {
      if (!m) return
      setAt(toDatetimeLocalValue(m.at))
      setType(m.type)
      setSources(m.sources)
      setNotes(m.notes ?? '')
      setCharge(true)
    })
  }, [id])

  // Pre-remplissage depuis une recette favorite (raccourci de l'accueil)
  useEffect(() => {
    const r = params.get('recette')
    if (!r || edition || charge) return
    setSources((s) =>
      s.some((x) => x.kind === 'recipe' && x.recipeId === r)
        ? s
        : [...s, { kind: 'recipe', recipeId: r, portions: 1 }],
    )
    setCharge(true)
  }, [params, edition, charge])

  const composition = useMemo(
    () => resoudreComposition(sources, recettes ?? []),
    [sources, recettes],
  )

  const nomIngredient = (idIng: string) =>
    ingredients?.find((i) => i.id === idIng)?.nom ?? 'Ingrédient supprimé'

  // Les deux listes restent separees et sont classees par pertinence. Melangees
  // et tronquees a huit, comme dans une premiere version, les recettes de
  // l'utilisateur passaient devant les aliments cherches et un ingredient au nom
  // frequent (« Pates de ble » parmi « pate feuilletee », « papaye »,
  // « parmesan »...) pouvait disparaitre du resultat.
  const recettesTrouvees = useMemo(
    () => (recherche.trim() ? filtrerRecettes(recettes ?? [], recherche).slice(0, 8) : []),
    [recherche, recettes],
  )
  const ingredientsTrouves = useMemo(
    () => (recherche.trim() ? filtrerIngredients(ingredients ?? [], recherche).slice(0, 30) : []),
    [recherche, ingredients],
  )
  const aDesResultats = recettesTrouvees.length > 0 || ingredientsTrouves.length > 0

  const ajouter = (r: { kind: 'recipe' | 'ingredient'; id: string }) => {
    setSources((s) => [
      ...s,
      r.kind === 'recipe'
        ? { kind: 'recipe', recipeId: r.id, portions: 1 }
        : { kind: 'ingredient', ingredientId: r.id, qte: 100, unite: 'g' },
    ])
    setRecherche('')
  }

  const retirer = (index: number) => setSources((s) => s.filter((_, i) => i !== index))

  const modifier = (index: number, patch: Partial<MealSource>) =>
    setSources((s) => s.map((x, i) => (i === index ? ({ ...x, ...patch } as MealSource) : x)))

  const enregistrer = async () => {
    if (sources.length === 0) return
    const data = {
      at: fromDatetimeLocalValue(at),
      type,
      sources,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    }
    if (id) await majRepas(id, data)
    else await creerRepas(data)
    navigate(-1)
  }

  return (
    <>
      <Entete titre={edition ? 'Modifier le repas' : 'Nouveau repas'} retour />

      <main className="contenu">
        <Carte>
          <div className="pile">
            <Champ label="Date et heure">
              <input
                type="datetime-local"
                value={at}
                onChange={(e) => setAt(e.target.value)}
              />
            </Champ>

            <div className="champ">
              <span className="champ__label">Moment</span>
              <div className="puces">
                {MEAL_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="choix"
                    aria-pressed={type === t}
                    onClick={() => setType(t)}
                  >
                    {MEAL_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Carte>

        <Carte titre="Contenu">
          <Champ label="Ajouter une recette ou un ingrédient">
            <input
              type="search"
              placeholder="Rechercher..."
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              autoComplete="off"
            />
          </Champ>

          {recherche.trim() && !aDesResultats && (
            <p className="doux" style={{ marginTop: 10 }}>
              Aucun résultat. Vous pouvez créer cet ingrédient depuis la Bibliothèque.
            </p>
          )}

          {ingredientsTrouves.length > 0 && (
            <>
              <p className="champ__label" style={{ marginTop: 12, marginBottom: 6 }}>
                Ingrédients
              </p>
              <ul className="liste">
                {ingredientsTrouves.map((i) => (
                  <li key={`ing-${i.id}`}>
                    <button
                      type="button"
                      className="item"
                      onClick={() => ajouter({ kind: 'ingredient', id: i.id })}
                    >
                      <span className="grandir item__principal">{i.nom}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {recettesTrouvees.length > 0 && (
            <>
              <p className="champ__label" style={{ marginTop: 12, marginBottom: 6 }}>
                Recettes
              </p>
              <ul className="liste">
                {recettesTrouvees.map((r) => (
                  <li key={`rec-${r.id}`}>
                    <button
                      type="button"
                      className="item"
                      onClick={() => ajouter({ kind: 'recipe', id: r.id })}
                    >
                      <span className="grandir item__principal">{r.nom}</span>
                      <span className="puce">
                        {r.items.length} ingrédient{r.items.length > 1 ? 's' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {sources.length === 0 ? (
            <Vide>Ajoutez au moins une recette ou un ingrédient.</Vide>
          ) : (
            <ul className="liste" style={{ marginTop: 12 }}>
              {sources.map((s, i) => (
                <li key={i} className="carte carte--plate" style={{ padding: 11 }}>
                  <div className="ligne ligne--espacee">
                    <span className="grandir item__principal">
                      {s.kind === 'recipe'
                        ? (recettes?.find((r) => r.id === s.recipeId)?.nom ?? 'Recette supprimée')
                        : nomIngredient(s.ingredientId)}
                    </span>
                    <button
                      type="button"
                      className="btn btn--fantome btn--petit"
                      onClick={() => retirer(i)}
                      aria-label="Retirer"
                    >
                      <IconeCroix />
                    </button>
                  </div>
                  <div className="ligne" style={{ marginTop: 8 }}>
                    <ChampNombre
                      decimal
                      min={0}
                      step={s.kind === 'recipe' ? 0.25 : 1}
                      valeur={s.kind === 'recipe' ? s.portions : s.qte}
                      onChange={(v) =>
                        modifier(i, s.kind === 'recipe' ? { portions: v } : { qte: v })
                      }
                      style={{ maxWidth: 110 }}
                      ariaLabel="Quantité"
                    />
                    {s.kind === 'recipe' ? (
                      <span className="doux">portion(s)</span>
                    ) : (
                      <select
                        value={s.unite}
                        onChange={(e) => modifier(i, { unite: e.target.value as Unite })}
                        style={{ maxWidth: 150 }}
                        aria-label="Unité"
                      >
                        {UNITES.map((u) => (
                          <option key={u} value={u}>
                            {UNITE_LABELS[u]}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Carte>

        {composition.length > 0 && (
          <Carte titre="Composition figée">
            <p className="doux" style={{ marginBottom: 10 }}>
              C'est cette liste qui sera enregistrée avec le repas. Modifier une recette plus tard
              ne changera pas ce qui est écrit ici.
            </p>
            <div className="puces">
              {composition.map((c) => (
                <span key={`${c.ingredientId}-${c.unite}`} className="puce">
                  {nomIngredient(c.ingredientId)} · {Math.round(c.qte * 10) / 10}{' '}
                  {UNITE_LABELS[c.unite]}
                </span>
              ))}
            </div>
          </Carte>
        )}

        <Carte>
          <Champ label="Notes (facultatif)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Contexte, ressenti, restaurant..."
            />
          </Champ>
        </Carte>

        <button
          type="button"
          className="btn btn--principal btn--large"
          onClick={() => void enregistrer()}
          disabled={sources.length === 0}
        >
          Enregistrer
        </button>

        {id && (
          <BoutonSupprimer
            titre="Supprimer ce repas ?"
            message="Le repas sera définitivement retiré du journal et des analyses."
            onSupprimer={() => {
              void supprimerRepas(id).then(() => navigate('/journal'))
            }}
          />
        )}
      </main>
    </>
  )
}
